// Administration (ADR 0007): roles, permissions, requests and responses of /api/admin.
// Permissions live here so that the API (which decides) and the admin console (which hides the buttons) read the same table.
import { z } from 'zod/v4';

export const ROLES = ['player', 'moderator', 'admin'] as const;
export const Role = z.enum(ROLES);
export type Role = z.infer<typeof Role>;

export const PERMISSIONS = [
  'overview:read',
  'moderation:read',
  'moderation:write',
  'users:read',
  /** Emails and linked accounts: personal data, reserved for administrators. */
  'users:pii',
  'users:suspend',
  'users:role',
  'audit:read',
  /** Rooms and races in progress on the game server. */
  'live:read',
  /** Events and tours: definitions, entrants, results. */
  'events:read',
  /** Launch reset: wipes every ride, creation, event, club and friendship, keeping the accounts. */
  'data:reset',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const MODERATOR: readonly Permission[] = ['overview:read', 'moderation:read', 'moderation:write', 'users:read', 'audit:read', 'live:read', 'events:read'];
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = { player: [], moderator: MODERATOR, admin: PERMISSIONS };

export const can = (role: Role | null | undefined, p: Permission): boolean => !!role && ROLE_PERMISSIONS[role].includes(p);
/** Role giving access to the console (at least one permission). */
export const isStaff = (role: Role | null | undefined): boolean => !!role && ROLE_PERMISSIONS[role].length > 0;

export const ADMIN_ACTIONS = ['user.suspend', 'user.unsuspend', 'user.role', 'creation.hide', 'creation.restore', 'data.reset'] as const;
export type AdminAction = (typeof ADMIN_ACTIONS)[number];

// ---------- Requests ----------
/** Reason for an administration action: mandatory, it is written to the audit log. */
export const AdminReason = z.string().trim().min(3, 'Reason: at least 3 characters').max(500);
export const AdminReasonBody = z.object({ reason: AdminReason });
export const AdminUsersQuery = z.object({
  q: z.string().trim().max(80).optional(),
  filter: z.enum(['all', 'suspended', 'staff']).default('all'),
  /** Cursor pagination: id of the last account received (exact creation date, then id, in the database). */
  after: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const AdminRoleBody = z.object({ role: Role, reason: AdminReason });
export const ModerationQuery = z.object({ status: z.enum(['open', 'hidden', 'all']).default('open') });
export const ModerateBody = z.object({ action: z.enum(['hide', 'restore']), reason: AdminReason });
/** Overview periods, in days (UTC). */
export const OVERVIEW_PERIODS = [7, 14, 30, 90] as const;
export const AdminOverviewQuery = z.object({
  days: z.coerce.number().int().refine((d): d is (typeof OVERVIEW_PERIODS)[number] => (OVERVIEW_PERIODS as readonly number[]).includes(d), 'Unknown period').default(14),
});
export const AuditQuery = z.object({
  /** Id of the last entry received. */
  after: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  target: z.string().max(60).optional(),
  action: z.enum(ADMIN_ACTIONS).optional(),
  /** Author (account of a team member). */
  actor: z.string().uuid().optional(),
});
/** Phrase typed by hand to confirm the launch reset: the API refuses the request without it. */
export const RESET_PHRASE = 'RESET';
export const AdminResetBody = z.object({ reason: AdminReason, confirm: z.literal(RESET_PHRASE) });
export const AdminEventsQuery = z.object({
  /** `upcoming`: dated start still to come; `past`: dated start gone by; `ondemand`: no date. */
  filter: z.enum(['all', 'upcoming', 'past', 'ondemand']).default('all'),
  q: z.string().trim().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export const AdminEventIdParam = z.object({ id: z.string().trim().min(1).max(60) });
export const AdminActivitiesQuery = z.object({
  /** Cursor: id of the last activity received (exact date, then id, in the database). */
  after: z.string().max(40).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});


export interface AdminMe { id: string; name: string; role: Role; permissions: Permission[] }

export interface AdminOverview {
  users: { total: number; new7d: number; active7d: number; suspended: number; staff: number };
  activities: { last24h: number; last7d: number };
  creations: { public: number; hidden: number; reported: number };
  ai: { last24h: number; failed24h: number };
  strava: { errors7d: number };
  /** Rooms and online players; null if the API runs without a game server. */
  live: { rooms: number; players: number } | null;
  /** Totals of the requested period and of the previous period of the same length (for the trends). */
  period: { days: number; signups: number; signupsPrev: number; activities: number; activitiesPrev: number; riders: number; ridersPrev: number };
  /** Sign-ups and activities per day (UTC) over the period, oldest to newest. */
  daily: { day: string; signups: number; activities: number }[];
}
/** Sidebar counters (null: without the permission). */
export interface AdminBadges { moderation: number | null }

export interface AdminUserRow {
  id: string; name: string; email: string | null; role: Role; createdAt: number;
  suspendedAt: number | null; xp: number; rides: number; lastActivityAt: number | null;
}
export interface AdminUserDetail extends AdminUserRow {
  suspendedReason: string | null;
  friendCode: string;
  profile: { ftp: number; mass: number; color: string };
  /** Linked providers (never the tokens); empty without the users:pii permission. */
  identities: { provider: string; linkedAt: number }[];
  counts: { activities: number; creations: number; friends: number; reportsMade: number };
  recentActivities: { id: string; date: number; type: string; name: string; xp: number }[];
  creations: { id: string; kind: string; name: string; visibility: string; hidden: boolean; reports: number; updatedAt: number }[];
  audit: AdminAuditEntry[];
}
export interface AdminReportedCreation {
  id: string; kind: string; name: string; desc: string; visibility: string; hidden: boolean; reports: number; updatedAt: number;
  author: { id: string; name: string };
  details: { reason: string; note: string | null; at: number; by: { id: string; name: string } }[];
}
export interface AdminAuditEntry {
  id: string; at: number; action: AdminAction; actor: { id: string | null; name: string };
  /** `system`: an action on the whole service (the launch reset). */
  target: { type: 'user' | 'creation' | 'system'; id: string; label: string };
  reason: string; data: Record<string, unknown>;
}

// ---------- Launch reset ----------
/**
 * What the launch reset erases, counted before it runs (GET) and as erased (POST). The accounts stay: users, linked
 * Google and Strava accounts, sessions, roles, the profile (name, weight, colour, look) and the audit log.
 */
export interface AdminResetCounts {
  /** Players whose progression (XP, achievements, coins, challenges, plan) goes back to zero. */
  players: number;
  activities: number; recordings: number; replays: number; ghosts: number; workouts: number; ratings: number;
  events: number; eventEntries: number; eventResults: number;
  creations: number; collection: number; generations: number;
  clubs: number; friendships: number;
}
export interface AdminResetPreview { counts: AdminResetCounts; accounts: number }

// ---------- Live rooms (game server memory, read-only) ----------
/** A rider in a room: `userId` links to the account; `rank`, `lap` and `finished` only while a race runs. */
export interface AdminLivePlayer {
  id: string; userId: string | null; name: string; cat: string | null; connected: boolean; host: boolean;
  rank: number | null; lap: number | null; laps: number | null; finished: boolean; out: boolean;
}
export interface AdminLiveRoom {
  code: string; mode: string; track: string; laps: number; bots: number;
  /** Opened at (ms). */
  since: number;
  event: { id: string; name: string; stage: number; stages: number; startAt: number | null } | null;
  /** Race clock in seconds while a race runs (negative during the countdown); null in the lobby. */
  elapsed: number | null;
  /** Riders in the race, bots included (null in the lobby). */
  riders: number | null;
  players: AdminLivePlayer[];
  spectators: number;
}
export interface AdminLive {
  /** False when the API runs without a game server in the same process. */
  available: boolean;
  rooms: AdminLiveRoom[];
  /** Open connections, in a room or not. */
  connections: number;
  draining: boolean;
}

// ---------- Events (read-only) ----------
export interface AdminEventRow {
  id: string; name: string; emoji: string; kind: 'event' | 'tour';
  /** `builtin`: one of the game's own events (library or calendar), not stored, listed because players entered it. */
  visibility: 'private' | 'link' | 'public' | 'builtin';
  owner: { id: string; name: string } | null;
  startAt: number | null; stages: number; categories: string[];
  entries: number;
  /** Stage results sent by players (one per player and stage). */
  results: number;
  updatedAt: number | null;
}
export interface AdminEventStage { index: number; name: string; mode: string; track: string; laps: number; results: number }
export interface AdminEventEntrant { userId: string; name: string; cat: string; joinedAt: number; stages: number; lastResultAt: number | null }
/** General classification of one category between players (bots removed), as the game computes it. */
export interface AdminEventStanding { cat: string; lines: { userId: string; name: string; rank: number; time: number; points: number; stages: number; complete: boolean }[] }
export interface AdminEventDetail extends AdminEventRow {
  desc: string; lateJoin: boolean; enforce: boolean; bots: number;
  stageList: AdminEventStage[];
  entrants: AdminEventEntrant[];
  standings: AdminEventStanding[];
}

// ---------- Activities of an account (read-only, never the samples) ----------
export interface AdminActivityRow {
  id: string; date: number; type: string; name: string; xp: number;
  mode: string | null; track: string | null; online: boolean; rank: number | null; riders: number | null;
  power: 'measured' | 'virtual' | 'simulated' | null;
  /** The results come from the game server's signed receipt; what the server noticed on the ride (ADR 0022). */
  verified: boolean; flags: string[];
  /** Summary figures: seconds, metres, watts. */
  duration: number; distance: number; avgPower: number; np: number; wkg: number; elevation: number;
  /** FTP and mass of the rider at the time. */
  ftp: number; mass: number;
  /** The server holds a replay of this activity (compressed size in bytes). */
  replay: number | null;
  strava: 'pending' | 'done' | 'error' | null;
}
